/** R3 of CONTACTS_REFACTOR — top-level Beneficiaries (Contacts) list.
 *
 * The dual-axis nav: Contacts ⊥ Campaigns. A beneficiary lives here
 * once and can be enrolled in many campaigns. Click a row → contact
 * detail page surfaces every campaign + every session this person
 * has been part of.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import { DateWindowPicker, SortSelect } from "@/components/ListSortAndDate";
import {
  emptyWindow,
  windowParams,
  type DateWindow,
} from "@/components/listParams";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { AppLayout } from "@/components/AppLayout";
import { BeneficiaryFormFields } from "@/components/BeneficiaryFormFields";
import {
  beneficiaryPayload,
  canSubmitBeneficiary,
  emptyBeneficiaryForm,
  type BeneficiaryForm,
} from "@/components/beneficiaryForm";
import { Pagination } from "@/components/Pagination";
import { PermissionGate } from "@/components/PermissionGate";
import { ApiPaths, api } from "@/lib/api";
import { describeApiError } from "@/lib/apiErrors";
import { displayPhone } from "@/lib/phone";
import type { Contact, Paginated } from "@/lib/types";

const PAGE_SIZE = 15;


export default function ContactsListPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [onlyDoNotCall, setOnlyDoNotCall] = useState(false);
  const [onlyInvalid, setOnlyInvalid] = useState(false);
  // Sorting + "who did I contact / see on <day>" (2026-09-29). Here a
  // contact can hold memberships across several campaigns, so the
  // backend aggregates over all of them and the contact appears once.
  const [sort, setSort] = useState("-created");
  const [contactedWindow, setContactedWindow] = useState<DateWindow>(emptyWindow);
  const [sessionWindow, setSessionWindow] = useState<DateWindow>(emptyWindow);
  const [showCreate, setShowCreate] = useState(false);
  const [page, setPage] = useState(1);

  // Back to the first page whenever a filter narrows the result set.
  useEffect(() => {
    setPage(1);
  }, [search, onlyDoNotCall, onlyInvalid, sort, contactedWindow, sessionWindow]);

  const contacts = useQuery({
    queryKey: [
      "contacts",
      search,
      onlyDoNotCall,
      onlyInvalid,
      sort,
      contactedWindow,
      sessionWindow,
      page,
    ],
    queryFn: async () => {
      const params: Record<string, string> = {
        page: String(page),
        page_size: String(PAGE_SIZE),
      };
      if (search.trim()) params.q = search.trim();
      if (onlyDoNotCall) params.do_not_call = "true";
      if (onlyInvalid) params.is_invalid = "true";
      if (sort) params.sort = sort;
      Object.assign(params, windowParams("contacted", contactedWindow));
      Object.assign(params, windowParams("session", sessionWindow));
      const res = await api.get<Paginated<Contact>>(ApiPaths.contacts, {
        params,
      });
      return res.data;
    },
    placeholderData: (prev) => prev,
  });
  const rows = contacts.data?.results ?? [];
  const total = contacts.data?.count ?? 0;

  return (
    <AppLayout
      eyebrow={t("contacts.eyebrow")}
      title={t("contacts.pageTitle")}
      description={t("contacts.pageDescription")}
      headerAction={
        <PermissionGate perm="campaign.members.manage">
          <button
            type="button"
            className="btn-primary text-sm"
            onClick={() => setShowCreate(true)}
          >
            {t("contacts.newButton")}
          </button>
        </PermissionGate>
      }
    >
      <div className="flex flex-wrap items-center gap-2">
        <input
          type="search"
          className="input max-w-[20rem]"
          placeholder={t("contacts.searchPlaceholder")}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <label className="flex items-center gap-1 text-xs text-nb-text-2">
          <input
            type="checkbox"
            checked={onlyDoNotCall}
            onChange={(e) => setOnlyDoNotCall(e.target.checked)}
          />
          {t("contacts.filterDoNotCall")}
        </label>
        <label className="flex items-center gap-1 text-xs text-nb-text-2">
          <input
            type="checkbox"
            checked={onlyInvalid}
            onChange={(e) => setOnlyInvalid(e.target.checked)}
          />
          {t("contacts.filterInvalid")}
        </label>
        <SortSelect
          value={sort}
          onChange={setSort}
          options={[
            "created",
            "contacted",
            "session_created",
            "session_ended",
            "session_started",
          ]}
        />
        <DateWindowPicker
          label={t("lists.contactedFilter")}
          value={contactedWindow}
          onChange={setContactedWindow}
          hint={t("lists.noRecordHint")}
        />
        <DateWindowPicker
          label={t("lists.sessionFilter")}
          value={sessionWindow}
          onChange={setSessionWindow}
        />
      </div>

      {contacts.isLoading && (
        <p className="card text-sm text-nb-text-3">{t("common.loading")}</p>
      )}
      {/* Without this, a fetch failure left `rows` empty with zero
          feedback — not even the (also silent) "no results" message,
          since that's gated on contacts.data being present. The
          operator just saw a blank list, indistinguishable from "still
          loading" once isLoading flipped false. */}
      {contacts.isError && (
        <p className="card text-sm text-cvp-danger">
          {t("contacts.loadError", "Could not load beneficiaries.")}
        </p>
      )}
      {contacts.data && total === 0 && (
        <p className="card text-sm text-nb-text-3">{t("contacts.empty")}</p>
      )}
      {rows.length > 0 && (
        <ul className="space-y-2">
          {rows.map((c) => {
            const name = `${c.first_name} ${c.last_name}`.trim();
            const initial = (name[0] || "?").toUpperCase();
            const secondary = [
              [c.city, c.country].filter(Boolean).join(" "),
              c.email,
              c.phone ? displayPhone(c.phone) : "",
            ].filter(Boolean);
            return (
              <li
                key={c.id}
                className="group flex items-center gap-4 rounded-lg border border-nb-border bg-nb-bg/60 px-4 py-3 transition-colors hover:border-nb-mint/40 hover:bg-nb-surface/40"
              >
                <Link
                  to={`/contacts/${c.id}`}
                  className="flex min-w-0 flex-1 items-center gap-4"
                >
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-nb-mint/10 text-sm font-semibold text-nb-mint">
                    {initial}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="truncate text-sm font-medium text-nb-text">
                        {name || "—"}
                      </span>
                      {c.is_invalid && (
                        <span className="pill text-[10px] border-cvp-warn/40 text-cvp-warn">
                          {t("contacts.flagInvalid")}
                        </span>
                      )}
                      {c.do_not_call && (
                        <span className="pill text-[10px] border-cvp-danger/40 text-cvp-danger">
                          {t("contacts.flagDoNotCall")}
                        </span>
                      )}
                      {c.gdpr_erasure_requested_at && (
                        <span className="pill text-[10px] border-cvp-warn/40 text-cvp-warn">
                          {t("contacts.flagGdpr")}
                        </span>
                      )}
                    </div>
                    {secondary.length > 0 && (
                      <p className="truncate text-xs text-nb-text-3">
                        {secondary.join(" · ")}
                      </p>
                    )}
                  </div>
                </Link>
                <div className="hidden shrink-0 items-center gap-1.5 text-xs sm:flex">
                  <span className="pill text-[10px]">
                    {c.membership_count}{" "}
                    {t("contacts.membershipCountWord", {
                      count: c.membership_count,
                    })}
                  </span>
                  {c.last_membership_status && (
                    <span className="text-nb-text-3">
                      {t(
                        `leads.status.${c.last_membership_status}`,
                        c.last_membership_status,
                      )}
                    </span>
                  )}
                </div>
                <PermissionGate perm="campaign.members.manage">
                  <Link
                    to={`/contacts/${c.id}/edit`}
                    className="btn-ghost shrink-0 text-xs"
                  >
                    {t("contacts.editButton", "Edit")}
                  </Link>
                </PermissionGate>
                <svg
                  className="hidden h-4 w-4 shrink-0 text-nb-text-3 transition-transform group-hover:translate-x-0.5 sm:block"
                  viewBox="0 0 20 20"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M7.5 4.5l6 5.5-6 5.5"
                  />
                </svg>
              </li>
            );
          })}
        </ul>
      )}

      <Pagination
        page={page}
        pageSize={PAGE_SIZE}
        count={total}
        onPage={setPage}
        disabled={contacts.isFetching}
      />

      {showCreate && (
        <NewContactModal onClose={() => setShowCreate(false)} qc={qc} />
      )}
    </AppLayout>
  );
}


function NewContactModal({
  onClose,
  qc,
}: {
  onClose: () => void;
  qc: ReturnType<typeof useQueryClient>;
}) {
  const { t } = useTranslation();
  const [form, setForm] = useState<BeneficiaryForm>(emptyBeneficiaryForm);
  const [customFields, setCustomFields] = useState<Record<string, string>>({});
  const create = useMutation({
    mutationFn: async () => {
      const res = await api.post<Contact>(
        ApiPaths.contacts,
        beneficiaryPayload(form, customFields),
      );
      return res.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["contacts"] });
      onClose();
    },
  });

  const canSubmit = canSubmitBeneficiary(form);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t("contacts.newButton")}
      className="modal-backdrop bg-black/70"
    >
      <div className="modal-panel w-full max-w-2xl max-h-[90vh] space-y-3 overflow-y-auto">
        <h2 className="text-lg font-semibold">{t("contacts.newTitle")}</h2>
        <BeneficiaryFormFields
          form={form}
          onChange={(patch) => setForm((f) => ({ ...f, ...patch }))}
          customFields={customFields}
          onCustomFieldChange={(key, value) =>
            setCustomFields((d) => ({ ...d, [key]: value }))
          }
        />
        {create.isError && (
          <p className="text-sm text-cvp-danger">
            {describeApiError(create.error, t("contacts.createErrorGeneric"))}
          </p>
        )}
        <div className="flex justify-end gap-2">
          <button type="button" className="btn-ghost text-sm" onClick={onClose}>
            {t("common.cancel")}
          </button>
          <button
            type="button"
            className="btn-primary text-sm"
            disabled={!canSubmit || create.isPending}
            onClick={() => create.mutate()}
          >
            {create.isPending ? t("common.saving") : t("common.save")}
          </button>
        </div>
      </div>
    </div>
  );
}
